import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  message,
  Form,
  Switch,
  Alert,
  Spin,
  InputNumber,
  Modal,
  Tooltip,
} from 'antd';
import { useKeycloak } from '@react-keycloak/web';
import {
  getPlatformSettings,
  updatePlatformSettings,
  syncCloudModels,
  getCurrentUserRoles,
} from '../services/api';
import RagQueryRewriteAdvancedOptions from '../components/collections/RagQueryRewriteAdvancedOptions';
import useCloudProviders from '../hooks/useCloudProviders';
import useLlmModelPicker from '../hooks/useLlmModelPicker';
import SettingsField, { SettingsBlock, SettingsPageHead, SettingsPanel } from '../components/settings/SettingsField';
import './AddAssistant.css';

const SETTINGS_CHAPTERS = [
  { id: 'general', label: 'General' },
  { id: 'defaults', label: 'Defaults' },
  { id: 'tracing', label: 'Tracing' },
];

const countFormat = new Intl.NumberFormat('en-US');
const relativeFormat = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function relativeTime(dateString) {
  if (!dateString) return 'Never synced';
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return 'Never synced';
  const minutes = Math.round((then - Date.now()) / 60000);
  const absMinutes = Math.abs(minutes);
  if (absMinutes < 60) return relativeFormat.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFormat.format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return relativeFormat.format(days, 'day');
  const months = Math.round(days / 30);
  if (Math.abs(months) < 12) return relativeFormat.format(months, 'month');
  return relativeFormat.format(Math.round(days / 365), 'year');
}

function exactTime(dateString) {
  if (!dateString) return 'Never synced';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Never synced';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ToggleRow({ label, help, helpId, checked, onChange }) {
  return (
    <div
      className="settings-field-row is-toggle"
      onClick={() => onChange(!checked)}
    >
      <div className="settings-field-copy">
        <div className="settings-field-name" id={`${helpId}-label`}>{label}</div>
        <div className="settings-field-help" id={helpId}>{help}</div>
      </div>
      <div className="settings-field-control" onClick={(event) => event.stopPropagation()}>
        <Switch
          checked={Boolean(checked)}
          onChange={onChange}
          aria-labelledby={`${helpId}-label`}
          aria-describedby={helpId}
        />
      </div>
    </div>
  );
}

function CloudModelsToggle({ checked, onChange, count }) {
  const request = (next) => {
    if (next || !count) {
      onChange(next);
      return;
    }
    const noun = count === 1 ? 'assistant uses' : 'assistants use';
    Modal.confirm({
      title: 'Turn off cloud models?',
      content: `${countFormat.format(count)} ${noun} a cloud model and will stop answering.`,
      okText: 'Turn off',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => onChange(false),
    });
  };
  return (
    <ToggleRow
      label="Cloud models"
      helpId="cloud-models-help"
      help="Let assistants use cloud providers. Requests are sent to the chosen provider. If this is off, only local models are available."
      checked={checked}
      onChange={request}
    />
  );
}

function PlatformSettings() {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState(null);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [isAdmin, setIsAdmin] = useState(false);
  const [form] = Form.useForm();
  const [settingsChapter, setSettingsChapter] = useState('general');
  const cloudOn = Form.useWatch('cloud_models_enabled', form);

  const { cloudProviders } = useCloudProviders(keycloak, { enabled: keycloak?.authenticated });
  const sessionTitlePicker = useLlmModelPicker({
    keycloak,
    cloudProviders,
    enabled: keycloak?.authenticated,
  });
  const promptAssistPicker = useLlmModelPicker({
    keycloak,
    cloudProviders,
    enabled: keycloak?.authenticated,
  });

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      checkAdminStatus();
      fetchSettings();
    }
  }, [initialized, keycloak]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const checkAdminStatus = async () => {
    try {
      const roles = await getCurrentUserRoles(keycloak);
      setIsAdmin(roles.is_admin);
    } catch (err) {
      console.error('Failed to check admin status:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getPlatformSettings(keycloak);
      setSettings(data);
      form.setFieldsValue({
        cloud_models_enabled: data.cloud_models_enabled,
        auto_sync_on_startup: data.auto_sync_on_startup,
        assistant_avatar_max_kb: data.assistant_avatar_max_kb ?? 2048,
        pipeline_trace_enabled: data.pipeline_trace_enabled ?? true,
        pipeline_trace_show_platform_chat: data.pipeline_trace_show_platform_chat ?? true,
        pipeline_trace_show_widget_admin: data.pipeline_trace_show_widget_admin ?? true,
        pipeline_trace_show_widget_embed: data.pipeline_trace_show_widget_embed ?? false,
      });
      sessionTitlePicker.hydrateFrom({
        is_local: data.widget_session_titles_is_local,
        model: data.widget_session_titles_model,
        cloud_provider_id: data.widget_session_titles_cloud_provider_id,
      });
      promptAssistPicker.hydrateFrom({
        is_local: data.prompt_assist_is_local ?? true,
        model: data.prompt_assist_model,
        cloud_provider_id: data.prompt_assist_cloud_provider_id,
      });
    } catch (err) {
      message.error(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      const stFields = sessionTitlePicker.toFields();
      const paFields = promptAssistPicker.toFields();
      const updated = await updatePlatformSettings(keycloak, {
        ...values,
        widget_session_titles_is_local: stFields.is_local,
        widget_session_titles_model: stFields.model,
        widget_session_titles_cloud_provider_id: stFields.cloud_provider_id,
        prompt_assist_is_local: paFields.is_local,
        prompt_assist_model: paFields.model,
        prompt_assist_cloud_provider_id: paFields.cloud_provider_id,
      });
      setSettings(updated);
      sessionTitlePicker.hydrateFrom({
        is_local: updated.widget_session_titles_is_local,
        model: updated.widget_session_titles_model,
        cloud_provider_id: updated.widget_session_titles_cloud_provider_id,
      });
      promptAssistPicker.hydrateFrom({
        is_local: updated.prompt_assist_is_local ?? true,
        model: updated.prompt_assist_model,
        cloud_provider_id: updated.prompt_assist_cloud_provider_id,
      });
      message.success('Settings saved successfully');
    } catch (err) {
      message.error(`Failed to save settings: ${err.message}`);
    }
  };

  const handleSync = async () => {
    if (!online) {
      setSyncNote({ kind: 'error', text: 'Offline. The current catalog is unchanged.' });
      return;
    }
    try {
      setSyncing(true);
      setSyncNote(null);
      const result = await syncCloudModels(keycloak);
      const stats = result?.stats || {};
      const added = stats.models_created || 0;
      const updated = stats.models_updated || 0;
      await fetchSettings();
      setSyncNote({
        kind: 'success',
        text: `Catalog updated. ${countFormat.format(added)} added, ${countFormat.format(updated)} updated.`,
      });
    } catch (err) {
      const lost = !navigator.onLine || /failed to fetch|network/i.test(err.message || '');
      setSyncNote({
        kind: 'error',
        text: lost ? 'No connection. Try again. The current catalog is unchanged.' : `${err.message || 'Sync failed.'} Try again.`,
      });
    } finally {
      setSyncing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page-container add-assistant-page">
        <Alert
          message="Access denied"
          description="You must be a platform administrator to access these settings."
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-container add-assistant-page">
      <div className="add-assistant-form-panel">
      <Form
          form={form}
          className="add-assistant-form-inner"
          layout="vertical"
          onFinish={handleSave}
          onFinishFailed={() => setSettingsChapter('general')}
          initialValues={{
            cloud_models_enabled: settings?.cloud_models_enabled ?? true,
            auto_sync_on_startup: settings?.auto_sync_on_startup ?? true,
            assistant_avatar_max_kb: settings?.assistant_avatar_max_kb ?? 2048,
            pipeline_trace_enabled: settings?.pipeline_trace_enabled ?? true,
            pipeline_trace_show_platform_chat: settings?.pipeline_trace_show_platform_chat ?? true,
            pipeline_trace_show_widget_admin: settings?.pipeline_trace_show_widget_admin ?? true,
            pipeline_trace_show_widget_embed: settings?.pipeline_trace_show_widget_embed ?? false,
            public_guest_shares_enabled: settings?.public_guest_shares_enabled ?? false,
            public_guest_daily_cap: settings?.public_guest_daily_cap,
            public_guest_per_share_cap: settings?.public_guest_per_share_cap,
          }}
        >
        <div className="settings-shell">
          <nav className="settings-side" role="tablist" aria-label="Settings">
            {SETTINGS_CHAPTERS.map((chapter) => (
              <button
                key={chapter.id}
                type="button"
                role="tab"
                aria-selected={settingsChapter === chapter.id}
                className={`settings-side-item${settingsChapter === chapter.id ? ' is-selected' : ''}`}
                onClick={() => {
                  setSettingsChapter(chapter.id);
                  window.scrollTo(0, 0);
                }}
              >
                {chapter.label}
              </button>
            ))}
          </nav>
          <div className="settings-main">
            <div className="add-assistant-page-intro">
              <h1 className="add-assistant-page-title">Platform settings</h1>
              <p className="add-assistant-page-subtitle">
                Limits, default models, and how much of the pipeline is recorded.
              </p>
            </div>

            <div className={`settings-chapter${settingsChapter === 'general' ? ' is-active' : ''}`}>
              <SettingsPageHead
                title="General"
                description="Upload limits, cloud models, and the shared model catalog."
              />
              <SettingsPanel>
                <SettingsField
                  label="Avatar size"
                  help="Largest image a person can upload for an assistant avatar."
                >
                  <Form.Item
                    name="assistant_avatar_max_kb"
                    rules={[{ required: true, message: 'Enter a limit' }]}
                    style={{ marginBottom: 0, width: '100%' }}
                  >
                    <InputNumber min={16} max={8192} addonAfter="KB" style={{ width: '100%' }} />
                  </Form.Item>
                </SettingsField>
              </SettingsPanel>
              <SettingsBlock title="Public guest chat shares">
                <SettingsPanel>
                  <Form.Item name="public_guest_shares_enabled" valuePropName="checked" noStyle>
                    <ToggleRow
                      label="Anyone with the link"
                      helpId="public-guest-shares-help"
                      help="When this is off, that choice is grayed out and logged-out visitors cannot open a shared chat."
                    />
                  </Form.Item>
                  <SettingsField
                    label="Daily guest cap"
                    help="How many messages logged-out visitors can send across all public links in a day."
                  >
                    <Form.Item name="public_guest_daily_cap" style={{ marginBottom: 0, width: '100%' }}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </SettingsField>
                  <SettingsField
                    label="Maximum per public link"
                    help="The highest continue cap a person can set on one public link."
                  >
                    <Form.Item name="public_guest_per_share_cap" style={{ marginBottom: 0, width: '100%' }}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </SettingsField>
                </SettingsPanel>
              </SettingsBlock>
              <SettingsBlock title="Cloud models">
                <SettingsPanel>
                  <Form.Item name="cloud_models_enabled" valuePropName="checked" noStyle>
                    <CloudModelsToggle count={settings?.cloud_assistant_count || 0} />
                  </Form.Item>
                  <Form.Item name="auto_sync_on_startup" valuePropName="checked" noStyle>
                    <ToggleRow
                      label="Update catalog on startup"
                      helpId="catalog-startup-help"
                      help="Refresh the model catalog when the platform starts."
                    />
                  </Form.Item>
                  <SettingsField
                    label="Catalog"
                    help={(
                      <>
                        <span className={`settings-catalog-figures${(cloudOn ?? settings?.cloud_models_enabled) === false ? ' is-muted' : ''}`}>
                          {countFormat.format(settings?.total_providers || 0)} providers
                          <span aria-hidden> · </span>
                          {countFormat.format(settings?.total_models || 0)} models
                        </span>
                        <span className="settings-catalog-note">
                          Last sync{' '}
                          <Tooltip title={exactTime(settings?.last_sync_at)}>
                            <span className="settings-catalog-time">{relativeTime(settings?.last_sync_at)}</span>
                          </Tooltip>
                        </span>
                        {(cloudOn ?? settings?.cloud_models_enabled) === false ? (
                          <span className="settings-catalog-note">Turn on cloud models to update the catalog.</span>
                        ) : null}
                        {!online ? (
                          <span className="settings-catalog-note is-error">Offline. The current catalog is unchanged.</span>
                        ) : null}
                        {syncNote ? (
                          <span className={`settings-catalog-note${syncNote.kind === 'error' ? ' is-error' : ''}`}>{syncNote.text}</span>
                        ) : null}
                      </>
                    )}
                  >
                    <Button
                      onClick={handleSync}
                      loading={syncing}
                      disabled={syncing || !online || (cloudOn ?? settings?.cloud_models_enabled) === false}
                    >
                      {syncing ? 'Syncing…' : 'Sync now'}
                    </Button>
                  </SettingsField>
                </SettingsPanel>
              </SettingsBlock>
            </div>

            <div className={`settings-chapter${settingsChapter === 'defaults' ? ' is-active' : ''}`}>
              <SettingsPageHead
                title="Defaults"
                description="Models used when the platform writes a title or edits a prompt."
              />
              <SettingsBlock>
                <SettingsPanel padded>
                  <RagQueryRewriteAdvancedOptions
                    compact
                    hideMaxMessages
                    sectionTitle="Session titles"
                    modelStepLabel="Model for session titles"
                    maxMessages={10}
                    onMaxMessagesChange={() => {}}
                    picker={sessionTitlePicker}
                    cloudProviders={cloudProviders}
                    navigate={navigate}
                    disabled={false}
                  />
                </SettingsPanel>
              </SettingsBlock>
              <SettingsBlock>
                <SettingsPanel padded>
                  <p className="settings-page-desc" style={{ marginBottom: 12 }}>
                    Used when someone asks the editor to shorten, improve, or structure a prompt.
                  </p>
                  <RagQueryRewriteAdvancedOptions
                    compact
                    hideMaxMessages
                    sectionTitle="Prompt assist"
                    modelStepLabel="Model for prompt assist"
                    maxMessages={10}
                    onMaxMessagesChange={() => {}}
                    picker={promptAssistPicker}
                    cloudProviders={cloudProviders}
                    navigate={navigate}
                    disabled={false}
                  />
                </SettingsPanel>
              </SettingsBlock>
            </div>

            <div className={`settings-chapter${settingsChapter === 'tracing' ? ' is-active' : ''}`}>
              <SettingsPageHead
                title="Tracing"
                description="What the backend records, and who can see those steps."
              />
              <SettingsPanel>
                <SettingsField
                  label="Record pipeline steps"
                  help="When on, the backend stores retrieval and model steps for debugging. Visibility still follows the rows below."
                >
                  <Form.Item name="pipeline_trace_enabled" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </SettingsField>
                <SettingsField
                  label="Platform chat"
                  help="Show recorded steps in the platform chat."
                >
                  <Form.Item name="pipeline_trace_show_platform_chat" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </SettingsField>
                <SettingsField
                  label="Widget history"
                  help="Show recorded steps in widget session history for signed-in admins."
                >
                  <Form.Item name="pipeline_trace_show_widget_admin" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </SettingsField>
                <SettingsField
                  label="Embedded widget"
                  help="Include traces in responses sent to site visitors. Traces can contain document snippets."
                >
                  <Form.Item name="pipeline_trace_show_widget_embed" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </SettingsField>
              </SettingsPanel>
            </div>

            <div className="add-assistant-form-footer">
              <Button type="primary" htmlType="submit">Save changes</Button>
            </div>
          </div>
        </div>
        </Form>
      </div>
    </div>
  );
}

export default PlatformSettings;
